import cn from 'classnames';
import { FC, RefObject, useEffect, useState } from 'react';

import { Icon, type IconNames } from '@/shared/ui';

import styles from './FullHeadMprToolCursorBadge.module.scss';

type FullHeadMprToolCursorBadgeProps = {
  // The positioned viewer container; the badge follows the pointer over its
  // panes ([data-full-head-mpr-pane]) only.
  containerRef: RefObject<HTMLDivElement>;
  icon: IconNames | null;
  className?: string;
};

type BadgePosition = { x: number; y: number };

export const FullHeadMprToolCursorBadge: FC<FullHeadMprToolCursorBadgeProps> = (
  props,
) => {
  const { containerRef, icon, className } = props;

  const [position, setPosition] = useState<BadgePosition | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !icon) {
      setPosition(null);
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const target = event.target;
      if (
        !(target instanceof Element) ||
        !target.closest('[data-full-head-mpr-pane]')
      ) {
        setPosition(null);
        return;
      }
      const rect = container.getBoundingClientRect();
      setPosition({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
    };
    const onPointerLeave = () => setPosition(null);

    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerleave', onPointerLeave);

    return () => {
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [containerRef, icon]);

  if (!icon || !position) {
    return null;
  }

  return (
    <div
      data-component="full-head-mpr-tool-cursor-badge"
      className={cn(styles.badge, className)}
      style={{ left: position.x, top: position.y }}
    >
      <Icon name={icon} size={16} />
    </div>
  );
};
